import { useEffect } from "react";
import { bounds, patchElements } from "./studioModel";
import { resizeElement } from "./studioResize";
import { transferToPage } from "./studioPageTransfer";
import { flowChildFor, reorderForDrop, containerAt, moveChildTo, setGap } from "./studioFlow";
import { layoutDocument } from "./core/structure";

// Canvas pointer gestures: move (snapping, cross-page transfer, menu-aware
// reordering), resize, rotate, marquee, pan and spacing handles. Every
// gesture previews transiently and commits once, as one undo step.
export default function useStudioGestures(context) {
  const { gesture, page, zoom, grid, snap, previewDoc, commit, commitAuthored, current, setSelected,
    workspaceRef, pageRefs, setMarquee, setDragVisual, setGuides, setActivePageId } = context;
  useEffect(() => {
    const move = (e) => {
      const g = gesture.current;
      if (!g || !page) return;
      e.preventDefault();
      if (g.type === "pan") {
        workspaceRef.current.scrollLeft = g.left - (e.clientX - g.clientX);
        workspaceRef.current.scrollTop = g.top - (e.clientY - g.clientY);
        return;
      }
      if (g.type === "move" && !g.dragStarted) {
        // Screen pixels keep click tolerance consistent at every canvas zoom.
        if (Math.hypot(e.clientX - g.clientX, e.clientY - g.clientY) < 4)
          return;
        g.dragStarted = true;
      }
      if (g.type === "move") {
        const workspace = workspaceRef.current;
        const rect = workspace.getBoundingClientRect();
        if (e.clientY > rect.bottom - 40) workspace.scrollTop += 18;
        else if (e.clientY < rect.top + 40) workspace.scrollTop -= 18;
      }
      const sourceRect = pageRefs.current.get(g.sourcePageId)?.querySelector("svg.ms-paper")?.getBoundingClientRect();
      const dx = g.type === "move" && sourceRect ? (e.clientX - sourceRect.left) / zoom - g.start.x : (e.clientX - g.clientX) / zoom,
        dy = g.type === "move" && sourceRect ? (e.clientY - sourceRect.top) / zoom - g.start.y : (e.clientY - g.clientY) / zoom;
      if (g.type === "marquee") {
        setMarquee({
          x: Math.min(g.start.x, g.start.x + dx),
          y: Math.min(g.start.y, g.start.y + dy),
          w: Math.abs(dx),
          h: Math.abs(dy),
        });
        return;
      }
      if (g.type === "gap") {
        const block = g.base.blocks[g.containerId];
        g.gap = Math.max(0, Math.round((g.startGap + dy) * 2) / 2);
        previewDoc(layoutDocument({ ...g.base, blocks: { ...g.base.blocks, [g.containerId]: setGap(block, g.key, g.gap, g.all ? "all" : "gap") } }));
        return;
      }
      const basePage = g.base.pages.find((p) => p.id === page.id);
      if (g.type === "move") {
        const moving = basePage.elements.filter((n) => g.ids.includes(n.id)),
          box = bounds(moving);
        if (!box) return;
        let mx = dx,
          my = dy;
        const lines = [];
        if (snap && !e.altKey) {
          const targets = {
            x: [
              0,
              page.margin,
              page.width / 2,
              page.width - page.margin,
              page.width,
            ],
            y: [
              0,
              page.margin,
              page.height / 2,
              page.height - page.margin,
              page.height,
            ],
          };
          basePage.elements
            .filter((n) => !g.ids.includes(n.id) && !n.hidden)
            .forEach((n) => {
              targets.x.push(n.x, n.x + n.w / 2, n.x + n.w);
              targets.y.push(n.y, n.y + n.h / 2, n.y + n.h);
            });
          for (const axis of ["x", "y"]) {
            const delta = axis === "x" ? dx : dy,
              dim = axis === "x" ? box.w : box.h;
            const positions = [
              box[axis] + delta,
              box[axis] + delta + dim / 2,
              box[axis] + delta + dim,
            ];
            let best = 5 / zoom,
              adjustment = 0,
              value;
            targets[axis].forEach((target) =>
              positions.forEach((pos) => {
                if (Math.abs(target - pos) < best) {
                  best = Math.abs(target - pos);
                  adjustment = target - pos;
                  value = target;
                }
              })
            );
            if (value !== undefined) {
              if (axis === "x") mx += adjustment;
              else my += adjustment;
              lines.push({ axis, value });
            } else if (grid) {
              if (axis === "x") mx = Math.round((box.x + dx) / 12) * 12 - box.x;
              else my = Math.round((box.y + dy) / 12) * 12 - box.y;
            }
          }
        }
        if (e.shiftKey) {
          if (Math.abs(dx) > Math.abs(dy)) my = 0;
          else mx = 0;
        }
        const moved = patchElements(g.base, g.ids, (n) => ({ x: n.x + mx, y: n.y + my }));
        previewDoc(moved, g.base);
        const workspace = workspaceRef.current.getBoundingClientRect();
        const ids = new Set(g.ids);
        const floating = moved.pages.flatMap((p) => p.elements).filter((n) => ids.has(n.id) || ids.has(n.attachedTo));
        setDragVisual({ nodes: floating, box: bounds(floating),
          left: workspace.left, top: workspace.top, width: workspace.width, height: workspace.height,
          x: (sourceRect?.left || 0) - workspace.left, y: (sourceRect?.top || 0) - workspace.top });
        setGuides(lines);
      }
      if (g.type === "resize") {
        const n = g.node,
          radians = (n.rotation * Math.PI) / 180;
        const lx = dx * Math.cos(radians) + dy * Math.sin(radians),
          ly = -dx * Math.sin(radians) + dy * Math.cos(radians);
        let w = Math.max(4, n.w + (g.corner.includes("e") ? lx : -lx)),
          h = Math.max(2, n.h + (g.corner.includes("s") ? ly : -ly));
        if (e.shiftKey || n.type === "image") h = (w * n.h) / n.w;
        const elements = g.base.pages.flatMap((p) => p.elements);
        previewDoc(patchElements(g.base, g.ids, (target) =>
          resizeElement(target, w / n.w, h / n.h, g.corner,
            elements.find((heading) => heading.id === target.attachedTo))
        ), g.base);
      }
      if (g.type === "rotate") {
        const n = g.node,
          p = { x: g.start.x + dx, y: g.start.y + dy };
        let rotation =
          n.rotation +
          ((Math.atan2(p.y - n.y - n.h / 2, p.x - n.x - n.w / 2) - g.angle) *
            180) /
            Math.PI;
        if (e.shiftKey) rotation = Math.round(rotation / 15) * 15;
        previewDoc(
          patchElements(g.base, [n.id], {
            rotation: Math.round(rotation * 10) / 10,
          }), g.base
        );
      }
    };
    const end = (e) => {
      const g = gesture.current;
      if (!g) return;
      if (e.type === "pointercancel") previewDoc(g.base, g.base);
      else if (g.type === "marquee") {
        const dx = (e.clientX - g.clientX) / zoom,
          dy = (e.clientY - g.clientY) / zoom;
        const box = {
          x: Math.min(g.start.x, g.start.x + dx),
          y: Math.min(g.start.y, g.start.y + dy),
          w: Math.abs(dx),
          h: Math.abs(dy),
        };
        const ids = page.elements
          .filter(
            (n) =>
              !n.hidden &&
              !n.locked &&
              n.x >= box.x &&
              n.y >= box.y &&
              n.x + n.w <= box.x + box.w &&
              n.y + n.h <= box.y + box.h
          )
          .map((n) => n.id);
        setSelected(Array.from(new Set([...g.initial, ...ids])));
      } else if (g.type === "gap") {
        if (g.gap !== undefined && g.gap !== g.startGap)
          commitAuthored((d) => ({ ...d, blocks: { ...d.blocks, [g.containerId]: setGap(d.blocks[g.containerId], g.key, g.gap, g.all ? "all" : "gap") } }));
        else previewDoc(null);
      } else if (g.type === "move" && g.dragStarted && g.base.blocks && flowChildFor(g.base, g.base.pages.find((p) => p.id === g.sourcePageId), g.ids)) {
        // A dish in a flowing section reorders or changes section; its
        // position comes from the layout, never from where it was dropped.
        const basePage = g.base.pages.find((p) => p.id === g.sourcePageId);
        const flow = flowChildFor(g.base, basePage, g.ids);
        const moved = current.current.pages.flatMap((p) => p.elements).find((n) => n.id === g.ids[0]);
        const origin = basePage.elements.find((n) => n.id === g.ids[0]);
        const dx = moved ? moved.x - origin.x : 0, dy = moved ? moved.y - origin.y : 0;
        const drop = { x: flow.child.box.x + flow.child.box.w / 2 + dx, y: flow.child.box.y + flow.child.box.h / 2 + dy };
        const target = containerAt(g.base, basePage, drop, flow.containerId);
        if (target && flow.child.groupId && !(g.base.pages.find((p) => p.id === g.sourcePageId).elements.some((n) => (n.groupPath || []).some((x) => x.id === flow.child.groupId) && (n.groupPath || []).some((x) => x.id === target.id)))) {
          commitAuthored((d) => moveChildTo(d, g.sourcePageId, flow.child, flow.containerId, target.id, drop));
        } else {
          const order = reorderForDrop(g.base, basePage, flow.containerId, flow.child.key, dx, dy);
          if (order) commitAuthored((d) => ({ ...d, blocks: { ...d.blocks, [flow.containerId]: { ...d.blocks[flow.containerId], layout: { ...d.blocks[flow.containerId].layout, order } } } }));
          else previewDoc(null);
        }
      } else if (g.type === "move" && !g.dragStarted && g.drillInto) {
        setSelected(g.drillInto, g.drillFocusId);
      } else if (g.type !== "pan" && (g.type !== "move" || g.dragStarted)) {
        const destination = g.type === "move" && [...pageRefs.current.entries()].find(([id, area]) => {
          if (id === g.sourcePageId) return false;
          const rect = area.querySelector("svg.ms-paper")?.getBoundingClientRect();
          return rect && e.clientX >= rect.left && e.clientX <= rect.right && e.clientY >= rect.top && e.clientY <= rect.bottom;
        });
        if (destination) {
          const [targetId, area] = destination;
          const rect = area.querySelector("svg.ms-paper").getBoundingClientRect();
          const ids = new Set(g.ids);
          const moving = g.base.pages.flatMap((p) => p.elements).filter((n) => ids.has(n.id) || ids.has(n.attachedTo));
          const box = bounds(moving);
          commit(transferToPage(g.base, g.ids, targetId, {
            x: (e.clientX - rect.left) / zoom - (g.start.x - box.x),
            y: (e.clientY - rect.top) / zoom - (g.start.y - box.y),
          }), g.base);
          setActivePageId(targetId);
          setSelected(g.ids);
        } else commit(current.current, g.base);
      }
      gesture.current = null;
      setDragVisual(null);
      setGuides([]);
      setMarquee(null);
    };
    window.addEventListener("pointermove", move, { passive: false });
    window.addEventListener("pointerup", end);
    window.addEventListener("pointercancel", end);
    return () => {
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", end);
      window.removeEventListener("pointercancel", end);
    };
  }, [page, zoom, grid, snap, previewDoc, commit, commitAuthored, current, setSelected, gesture, pageRefs, setActivePageId, setDragVisual, setGuides, setMarquee, workspaceRef]);
}
